import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles.css';
import { initTheme } from './lib/theme.js';

// Apply the stored theme before first render (flash-free) — A7.
initTheme();

import { FEATURES } from '@dgm/shared';
import { AppShell } from './components/AppShell.js';
import { LoginPage } from './routes/login.js';
import { PlayPage } from './routes/play.js';
import { MpDevPage } from './routes/mp-dev.js';
import { SignupPage } from './routes/signup.js';
import { VerifyPage } from './routes/verify.js';
import { ForgotPasswordPage } from './routes/forgot-password.js';
import { ResetPasswordPage } from './routes/reset-password.js';

// Code-split the authenticated route bodies (TE-007, the admin main.tsx
// pattern): each becomes its own chunk, shrinking the entry bundle. The auth
// flow + the PUBLIC /play viewer stay eager — they ARE the entry surfaces.
const DashboardPage = lazy(() =>
  import('./routes/dashboard.js').then((m) => ({ default: m.DashboardPage })),
);
const NewGamePage = lazy(() => import('./routes/new.js').then((m) => ({ default: m.NewGamePage })));
const GameDetailPage = lazy(() =>
  import('./routes/game.js').then((m) => ({ default: m.GameDetailPage })),
);
const FriendsPage = lazy(() =>
  import('./routes/friends.js').then((m) => ({ default: m.FriendsPage })),
);
const SettingsPage = lazy(() =>
  import('./routes/settings.js').then((m) => ({ default: m.SettingsPage })),
);
const HelpPage = lazy(() => import('./routes/help.js').then((m) => ({ default: m.HelpPage })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const lazyRoute = (el: React.ReactNode) => (
  <Suspense fallback={<div className="p-8 text-muted-foreground">Loading…</div>}>{el}</Suspense>
);

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // PUBLIC published-game viewer (D-138) — no auth, outside AppShell.
  { path: '/play/:slug', element: <PlayPage /> },
  // Multiplayer DEV-WINDOW viewer (MP-006) — registered only inside the
  // time-boxed window; FEATURES.multiplayerDevWindow is false by default.
  ...(FEATURES.multiplayerDevWindow ? [{ path: '/mp-dev', element: <MpDevPage /> }] : []),
  { path: '/signup', element: <SignupPage /> },
  { path: '/verify', element: <VerifyPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: lazyRoute(<DashboardPage />) },
      { path: 'new', element: lazyRoute(<NewGamePage />) },
      { path: 'games/:gameId', element: lazyRoute(<GameDetailPage />) },
      { path: 'friends', element: lazyRoute(<FriendsPage />) },
      { path: 'settings', element: lazyRoute(<SettingsPage />) },
      { path: 'help', element: lazyRoute(<HelpPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
